/**
 * eventSource — the label shown for a security event's source.
 *
 * Why this exists: many events the edge agent sends are generated by the agent
 * itself rather than observed on the wire — its own Modbus active polls, service
 * discovery, asset discovery, exposure summaries, CISA KEV sync/enrichment and
 * enforcement actions. On those, source_ip is usually absent:
 *   - active polls carry the agent's own interface IP when the agent can resolve
 *     it, and omit source_ip when it cannot — never the node name, which used to
 *     pollute downstream consumers (ai_sentry_agent modbus_dpi._agent_source_ip);
 *   - the report-type events (discovery, summaries, KEV, unblock) set no source.
 * Their provenance is in the event: details.event_source, details.self_originated,
 * triggering_event.source_role, or the event_type itself. Rendering
 * `source_ip || 'N/A'` hid that the source is known: it is the agent. L2 events
 * (IEC 61850 GOOSE) have no IP at all, only a MAC.
 *
 * Order: a real source_ip wins; then agent provenance; then a MAC; then the
 * genuinely-missing text.
 *
 * ANNOTATIONS — the three tables below are maintained by hand, because the set
 * of values is decided by the agent, not by this repo. Derived from the agent at
 * 07755c6 by grepping the bare token `event_source` and `event_type=` under
 * agent/. When the agent gains a new event_source or event_type, classify it
 * here (agent-generated → AGENT_EVENT_SOURCES / AGENT_REPORT_TYPES; observed on
 * the wire → OBSERVED_EVENT_SOURCES). eventSource.test.js runs this classifier
 * over captured real rows and fails on any unclassified value, and on any
 * agent-generated row that would read "Not reported".
 */

export const SOURCE_NOT_REPORTED = 'Not reported';
export const EDGE_AGENT_ACTIVE_POLL = 'Edge agent (active poll)';
export const EDGE_AGENT = 'Edge agent';

/** details.event_source values the agent sets on events it generates itself. */
export const AGENT_EVENT_SOURCES = {
  modbus_active_poll: EDGE_AGENT_ACTIVE_POLL,          // modbus_dpi.py _poll_event_provenance
  service_discovery: 'Edge agent (discovery)',         // network_monitor.py synthetic policy events
  exposure_summary: 'Edge agent (summary)',            // exposure_analytics.py
  cisa_kev_sync: 'Edge agent (KEV sync)',              // cisa_kev.py _send_sync_event
  cisa_kev_enrichment: 'Edge agent (KEV enrichment)',  // cisa_kev.py VulnerabilityAlert
};

/** details.event_source values for traffic the agent observed (the source is the frame's). */
export const OBSERVED_EVENT_SOURCES = new Set([
  'live_traffic_capture',                              // traffic_utils.py, network_monitor.py GOOSE
]);

/** event_types the agent emits as its own reports (some carry no event_source). */
export const AGENT_REPORT_TYPES = {
  AssetDiscovered: 'Edge agent (discovery)',           // network_monitor.py
  OTExposureSummary: 'Edge agent (summary)',           // network_monitor.py, exposure_analytics.py
  CISAKEVSync: 'Edge agent (KEV sync)',                // cisa_kev.py
  VulnerabilityAlert: 'Edge agent (KEV enrichment)',   // cisa_kev.py
  EnforcementAction: 'Edge agent (enforcement)',       // enforcement.py (unblock sets no source)
};

const has = (obj, key) => key != null && Object.prototype.hasOwnProperty.call(obj, key);

/** The event's details.event_source (or the triggering event's), or null. */
export function eventSourceOf(event) {
  const d = (event && event.details) || {};
  const te = d.triggering_event || {};
  return d.event_source || te.event_source || null;
}

/** True when the event was produced by the edge agent itself, not observed on the wire. */
export function isAgentOriginated(event) {
  if (!event) return false;
  const d = event.details || {};
  const te = d.triggering_event || {};
  return d.self_originated === true
    || te.self_originated === true
    || te.source_role === 'EdgeAgent'
    || has(AGENT_EVENT_SOURCES, eventSourceOf(event))
    || has(AGENT_REPORT_TYPES, event.event_type);
}

/**
 * The display text for an event's source. Accepts both the /events shape
 * (`source_ip`) and the /topology/events shape (`source_ip`, `source_mac`).
 */
export function eventSourceLabel(event) {
  if (!event) return SOURCE_NOT_REPORTED;
  if (event.source_ip && event.source_ip !== 'N/A') return event.source_ip;

  if (isAgentOriginated(event)) {
    const src = eventSourceOf(event);
    if (has(AGENT_EVENT_SOURCES, src)) return AGENT_EVENT_SOURCES[src];
    return AGENT_REPORT_TYPES[event.event_type] || EDGE_AGENT;
  }

  const te = (event.details && event.details.triggering_event) || {};
  const mac = event.source_mac || te.source_mac;
  if (mac) return mac;

  return SOURCE_NOT_REPORTED;
}

// The briefing model's row and prompt rules live in briefingPrompt.js (WP11).
